import React, { useState, useEffect } from 'react';
import { Facebook, AlertCircle, X, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getFacebookCredentials } from '../lib/apiCredentials';
import { exchangeForLongLivedToken } from '../lib/metaTokens';
import toast from 'react-hot-toast';

interface FacebookConnectProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  hasConnectedAccounts?: boolean;
}

interface FacebookAccount {
  id: string;
  name: string;
  access_token: string;
  type: 'page' | 'profile' | 'group';
  category?: string;
}

interface DeleteConfirmationProps {
  account: FacebookAccount;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({ account, onConfirm, onCancel }) => (
  <div className="absolute right-0 top-0 mt-8 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
    <div className="flex justify-between items-start mb-3">
      <h3 className="text-sm font-medium text-gray-900">Delete Account</h3>
      <button
        onClick={onCancel}
        className="text-gray-400 hover:text-gray-500"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
    <p className="text-sm text-gray-500 mb-4">
      Are you sure you want to remove "{account.name}"? This will also delete all scheduled tasks for this account.
    </p>
    <div className="flex justify-end space-x-2">
      <button
        onClick={onCancel}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
      >
        Delete
      </button>
    </div>
  </div>
);

export function FacebookConnect({ onSuccess, onError, hasConnectedAccounts }: FacebookConnectProps) {
  const [loading, setLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<FacebookAccount[]>([]);
  const [availableAccounts, setAvailableAccounts] = useState<FacebookAccount[]>([]);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [connectedAccountIds, setConnectedAccountIds] = useState<string[]>([]);
  const [accountType, setAccountType] = useState<'page' | 'profile' | 'group'>('page');

  useEffect(() => {
    loadConnectedAccounts();

    const loadFacebookSDK = async () => {
      const { appId } = await getFacebookCredentials();
      console.log('🔑 Loading Facebook SDK with App ID:', appId);
      if (!appId) {
        console.error('Facebook App ID not found');
        return;
      }

      const existingScript = document.getElementById('facebook-jssdk');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      
      script.onload = () => {
        const FB = (window as any).FB;
        if (!FB) {
          console.error('Facebook SDK not loaded properly');
          return;
        }

        FB.init({
          appId: appId,
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
      };

      document.body.appendChild(script);
    };

    loadFacebookSDK();

    return () => {
      const existingScript = document.getElementById('facebook-jssdk');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const loadConnectedAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check both connected_accounts (old system) and social_accounts (new system)
      const [connectedAccountsResult, socialAccountsResult] = await Promise.all([
        supabase
          .from('connected_accounts')
          .select('id')
          .eq('platform', 'facebook')
          .eq('user_id', user.id),
        supabase
          .from('social_accounts')
          .select('page_id')
          .eq('platform', 'facebook')
          .eq('owner_user_id', user.id)
      ]);

      const connectedIds = connectedAccountsResult.data?.map(a => a.id) || [];
      const socialIds = socialAccountsResult.data?.map(a => a.page_id) || [];

      // Combine both lists and remove duplicates
      const allIds = [...new Set([...connectedIds, ...socialIds])];

      console.log('Connected account IDs (old system):', connectedIds);
      console.log('Social account IDs (new system):', socialIds);
      console.log('Combined unique IDs:', allIds);

      setConnectedAccountIds(allIds);
    } catch (error) {
      console.error('Error loading connected accounts:', error);
    }
  };

  const getPermissionsForAccountType = (type: 'page' | 'profile' | 'group') => {
    switch (type) {
      case 'page':
        return 'public_profile,email,pages_show_list,pages_read_engagement,pages_manage_posts,pages_read_user_content,pages_manage_ads,ads_management';
      case 'profile':
        return 'public_profile,email,user_posts';
      case 'group':
        return 'public_profile,email,groups_access_member_info,publish_to_groups';
      default:
        return 'public_profile,email';
    }
  };

  const handleFacebookLogin = async () => {
    setLoading(true);
    try {
      const FB = (window as any).FB;
      if (!FB) throw new Error('Facebook SDK not loaded');

      // Reset login state
      await new Promise<void>((resolve) => {
        FB.getLoginStatus((response: any) => {
          if (response.status === 'connected') {
            FB.logout(() => resolve());
          } else {
            resolve();
          }
        });
      });

      // Request permissions based on account type
      const permissions = getPermissionsForAccountType(accountType);
      console.log(`Requesting Facebook permissions for ${accountType}:`, permissions);

      const response: any = await new Promise((resolve, reject) => {
        FB.login((response: any) => {
          if (response.authResponse) {
            console.log('Facebook login successful, authResponse:', {
              userID: response.authResponse.userID,
              grantedScopes: response.authResponse.grantedScopes
            });
            resolve(response.authResponse);
          } else {
            console.warn('Facebook login cancelled or failed:', response);
            reject(new Error('Facebook login cancelled'));
          }
        }, {
          scope: permissions,
          auth_type: 'rerequest',
          return_scopes: true
        });
      });

      // Get long-lived token - wymiana po stronie serwera, App Secret nie trafia do bundla
      console.log('Exchanging for long-lived token...');
      const longLivedToken = await exchangeForLongLivedToken(response.accessToken, 'facebook');
      console.log('Long-lived token obtained successfully');

      const accounts: FacebookAccount[] = [];
      let allPages: any[] = [];
      let allGroups: any[] = [];

      switch (accountType) {
        case 'page':
          // Get all pages with pagination
          let nextUrl: string | null = null;

          do {
            const pagesResponse: any = await new Promise((resolve, reject) => {
              const params = nextUrl ? nextUrl : '/me/accounts?limit=100';
              FB.api(params, { access_token: longLivedToken }, (response: any) => {
                if (response.error) {
                  console.error('Facebook API error:', response.error);
                  reject(response.error);
                } else {
                  resolve(response);
                }
              });
            });

            console.log('Facebook pages API response:', pagesResponse);

            if (pagesResponse.data && Array.isArray(pagesResponse.data)) {
              allPages = allPages.concat(pagesResponse.data);
              console.log(`Loaded ${pagesResponse.data.length} Facebook pages, total: ${allPages.length}`);
            } else {
              console.warn('No data in pages response:', pagesResponse);
            }

            nextUrl = pagesResponse.paging?.next || null;
          } while (nextUrl);

          console.log(`Total Facebook pages found: ${allPages.length}`);
          console.log(`Already connected page IDs:`, connectedAccountIds);

          if (allPages.length > 0) {
            const pages = allPages
              .map((page: any) => ({
                id: page.id,
                name: page.name,
                access_token: page.access_token,
                type: 'page' as const,
                category: page.category
              }))
              .filter((page: FacebookAccount) => {
                const isConnected = connectedAccountIds.includes(page.id);
                if (isConnected) {
                  console.log(`Page "${page.name}" (${page.id}) is already connected`);
                }
                return !isConnected;
              });

            console.log(`Available new pages to connect: ${pages.length}`);
            accounts.push(...pages);
          }
          setAvailableAccounts(accounts);
          setShowAccountSelector(accounts.length > 0);
          break;

        case 'profile':
          // Get user profile and connect directly
          const profileResponse: any = await new Promise((resolve, reject) => {
            FB.api('/me', { access_token: longLivedToken }, (response: any) => {
              if (response.error) reject(response.error);
              else resolve(response);
            });
          });

          if (!connectedAccountIds.includes(profileResponse.id)) {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const { error: dbError } = await supabase
              .from('connected_accounts')
              .insert({
                id: profileResponse.id,
                name: profileResponse.name,
                platform: 'facebook',
                account_type: 'profile',
                access_token: longLivedToken,
                user_id: user.id,
                metadata: {
                  type: 'profile'
                }
              });

            if (dbError && dbError.code !== '23505') { // Ignore unique violation
              throw dbError;
            }

            onSuccess?.();
            toast.success('Facebook profile connected successfully');
          } else {
            toast.error('Facebook profile already connected');
          }
          break;

        case 'group':
          // Get groups where user is admin with pagination
          let nextGroupUrl: string | null = null;

          do {
            const groupsResponse: any = await new Promise((resolve, reject) => {
              const params = nextGroupUrl ? nextGroupUrl : '/me/groups?limit=100&fields=id,name,administrator';
              FB.api(params, {
                access_token: longLivedToken
              }, (response: any) => {
                if (response.error) reject(response.error);
                else resolve(response);
              });
            });

            if (groupsResponse.data && Array.isArray(groupsResponse.data)) {
              const adminGroups = groupsResponse.data.filter((group: any) => group.administrator);
              allGroups = allGroups.concat(adminGroups);
              console.log(`Loaded ${adminGroups.length} admin groups, total: ${allGroups.length}`);
            }

            nextGroupUrl = groupsResponse.paging?.next || null;
          } while (nextGroupUrl);

          console.log(`Total admin groups found: ${allGroups.length}`);
          console.log(`Already connected group IDs:`, connectedAccountIds);

          if (allGroups.length > 0) {
            const groups = allGroups
              .map((group: any) => ({
                id: group.id,
                name: group.name,
                access_token: longLivedToken,
                type: 'group' as const
              }))
              .filter((group: FacebookAccount) => {
                const isConnected = connectedAccountIds.includes(group.id);
                if (isConnected) {
                  console.log(`Group "${group.name}" (${group.id}) is already connected`);
                }
                return !isConnected;
              });

            console.log(`Available new groups to connect: ${groups.length}`);
            accounts.push(...groups);
          }
          setAvailableAccounts(accounts);
          setShowAccountSelector(accounts.length > 0);
          break;
      }

      if (accounts.length === 0 && accountType !== 'profile') {
        if (accountType === 'page' && allPages.length > 0) {
          toast.error(`All ${allPages.length} Facebook pages are already connected`);
        } else if (accountType === 'group' && allGroups.length > 0) {
          toast.error(`All ${allGroups.length} Facebook groups are already connected`);
        } else {
          toast.error(`No new Facebook ${accountType}s available to connect`);
        }
        setShowAccountSelector(false);
      }
    } catch (err) {
      console.error('Facebook login error:', err);
      onError?.(err instanceof Error ? err.message : 'Failed to connect Facebook account');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelection = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select at least one account');
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      for (const account of selectedAccounts) {
        // Check if account is already connected to a different user
        const { data: existingAccount } = await supabase
          .from('connected_accounts')
          .select('user_id, name')
          .eq('id', account.id)
          .eq('platform', 'facebook')
          .maybeSingle();

        if (existingAccount && existingAccount.user_id === user.id) {
          console.log(`Account ${account.name} is already connected to current user`);
          toast.success(`${account.name} jest już podłączony do Twojego konta`);
          continue;
        }

        // If account belongs to another user, ask for confirmation and takeover
        if (existingAccount && existingAccount.user_id !== user.id) {
          const confirmed = window.confirm(
            `${account.name} jest już podłączony do innego użytkownika. Czy chcesz przejąć kontrolę?\n\nUwaga: To odłączy konto od poprzedniego właściciela.`
          );

          if (!confirmed) {
            console.log(`User declined to take over ${account.name}`);
            continue;
          }

          // Call edge function to transfer account (requires service role to bypass RLS)
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          const transferResponse = await fetch(`${supabaseUrl}/functions/v1/transfer-account`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({
              accountId: account.id,
              newUserId: user.id,
              platform: 'facebook',
            }),
          });

          if (!transferResponse.ok) {
            const error = await transferResponse.json();
            console.error('Failed to transfer account:', error);
            toast.error(`Nie udało się przejąć ${account.name}`);
            continue;
          }

          // Update access_token after transfer (edge function only changes owner)
          const { error: updateError } = await supabase
            .from('connected_accounts')
            .update({
              access_token: account.access_token,
              updated_at: new Date().toISOString(),
            })
            .eq('id', account.id);

          if (updateError) {
            console.error('Error updating access token:', updateError);
            toast.error(`Przeniesiono ${account.name}, ale nie udało się zaktualizować tokenu`);
          } else {
            toast.success(`${account.name} został przeniesiony z innego konta`);
          }

          continue; // Skip UPSERT since we already handled transfer
        }

        // Use UPSERT only for NEW accounts or accounts already owned by current user
        const { error: dbError } = await supabase
          .from('connected_accounts')
          .upsert({
            id: account.id,
            name: account.name,
            platform: 'facebook',
            account_type: account.type,
            access_token: account.access_token,
            user_id: user.id,
            metadata: {
              category: account.category,
              type: account.type
            }
          }, {
            onConflict: 'id',
            ignoreDuplicates: false
          });

        if (dbError) {
          console.error('Error inserting account:', dbError);
          throw dbError;
        }
      }

      setShowAccountSelector(false);
      onSuccess?.();
      toast.success(`Facebook ${accountType}s connected successfully`);
    } catch (error) {
      console.error('Error saving accounts:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to save Facebook accounts');
    } finally {
      setLoading(false);
    }
  };

  if (showAccountSelector) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Select Facebook {accountType === 'page' ? 'Pages' : accountType === 'profile' ? 'Profile' : 'Groups'}</h3>
        <p className="text-sm text-gray-500">Select the {accountType}s you want to manage</p>
        
        <div className="max-h-60 overflow-y-auto">
          {availableAccounts.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
                <p className="ml-3 text-sm text-yellow-700">
                  No new Facebook {accountType}s found. All your {accountType}s are already connected.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {availableAccounts.map((account) => (
                <label key={account.id} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedAccounts.some(p => p.id === account.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAccounts([...selectedAccounts, account]);
                      } else {
                        setSelectedAccounts(selectedAccounts.filter(p => p.id !== account.id));
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    {account.name}
                    {account.category && (
                      <span className="ml-2 text-xs text-gray-500">({account.category})</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={() => setShowAccountSelector(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAccountSelection}
            disabled={!selectedAccounts.length || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Connecting...' : 'Connect Selected Accounts'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <button
          onClick={() => {
            setAccountType('page');
            handleFacebookLogin();
          }}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg
            text-gray-500 hover:text-blue-600 hover:border-blue-600
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full"
        >
          {loading && accountType === 'page' ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Facebook className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">Connect Page</span>
            </>
          )}
        </button>

        <button
          onClick={() => {
            setAccountType('profile');
            handleFacebookLogin();
          }}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg
            text-gray-500 hover:text-blue-600 hover:border-blue-600
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full"
        >
          {loading && accountType === 'profile' ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Facebook className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">Connect Profile</span>
            </>
          )}
        </button>

        <button
          onClick={() => {
            setAccountType('group');
            handleFacebookLogin();
          }}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg
            text-gray-500 hover:text-blue-600 hover:border-blue-600
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full"
        >
          {loading && accountType === 'group' ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Facebook className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">Connect Group</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}